import { useCallback, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { RefreshCw } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { PageContainer, PageHeader } from '../components/layout/Page';
import WorkView from '../components/dashboard/WorkView';
import ComplianceView from '../components/dashboard/ComplianceView';
import { cachedAt, useAsyncData } from '../components/dashboard/useAsyncData';
import { getWorkloadData } from '../components/dashboard/workload';
import { getComplianceData } from '../components/dashboard/compliance';
import { getDashboardTrends } from '../components/dashboard/dashboardTrends';
import { controlClass } from '../components/dashboard/ui';
import { api } from '../services/api';
import { summarizeCoverage } from '../utils/dashboardProgress';

type View = 'trabajo' | 'cumplimiento';
const STORAGE_KEY = 'repse-dashboard-view';
const VIEWS: { id: View; label: string }[] = [
  { id: 'trabajo', label: 'Mi trabajo' },
  { id: 'cumplimiento', label: 'Cumplimiento' },
];

function readStoredView(): View | null {
  try {
    const value = localStorage.getItem(STORAGE_KEY);
    return value === 'trabajo' || value === 'cumplimiento' ? value : null;
  } catch {
    return null;
  }
}

function WorkTab({ refreshKey, userId }: { refreshKey: number; userId: string | null }) {
  const workload = useAsyncData(() => getWorkloadData(userId), refreshKey, `workload:${userId}`);
  const trends = useAsyncData(getDashboardTrends, refreshKey, 'trends');
  return <WorkView workload={workload} trends={trends} />;
}

function ComplianceTab({ refreshKey }: { refreshKey: number }) {
  const compliance = useAsyncData(() => getComplianceData(), refreshKey, 'compliance');
  const year = new Date().getFullYear();
  const coverage = useAsyncData(() => api.getAdminReportData(year).then((data) => summarizeCoverage(data, year)), refreshKey, `coverage:${year}`);
  return <ComplianceView compliance={compliance} coverage={coverage} />;
}

export default function Dashboard() {
  const { userRole, user } = useAuth();
  const isAdmin = userRole === 'admin';
  const [params, setParams] = useSearchParams();
  const requested = params.get('vista');
  const view: View = !isAdmin ? 'trabajo' : requested === 'trabajo' || requested === 'cumplimiento' ? requested : readStoredView() || 'trabajo';
  const [refreshKey, setRefreshKey] = useState(0);
  const [updatedAt, setUpdatedAt] = useState(() => cachedAt(view === 'trabajo' ? `workload:${user?.id ?? null}` : 'compliance') || new Date());

  const choose = (next: View) => {
    try { localStorage.setItem(STORAGE_KEY, next); } catch { /* preference only */ }
    setParams((current) => { const copy = new URLSearchParams(current); copy.set('vista', next); return copy; }, { replace: true });
  };
  const refresh = useCallback(() => {
    setRefreshKey((key) => key + 1);
    setUpdatedAt(new Date());
  }, []);
  const active = VIEWS.find((v) => v.id === view)!;

  return (
    <PageContainer width="wide">
      <PageHeader
        title={active.label}
        description={`Actualizado a las ${updatedAt.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })}`}
        actions={
          <>
            {isAdmin && (
              <div role="tablist" aria-label="Vista del resumen" className="inline-flex rounded-lg border border-slate-300 bg-white p-0.5">
                {VIEWS.map((v) => (
                  <button
                    key={v.id}
                    type="button"
                    role="tab"
                    aria-selected={view === v.id}
                    aria-controls="dashboard-panel"
                    onClick={() => choose(v.id)}
                    className={`rounded-md px-3 py-1.5 text-sm font-medium focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600 ${view === v.id ? 'bg-slate-900 text-white' : 'text-slate-600 hover:text-slate-900'}`}
                  >
                    {v.label}
                  </button>
                ))}
              </div>
            )}
            <button type="button" onClick={refresh} className={`${controlClass} inline-flex items-center gap-2`}>
              <RefreshCw aria-hidden="true" className="h-4 w-4" />
              Actualizar
            </button>
          </>
        }
      />
      <div id="dashboard-panel" role={isAdmin ? 'tabpanel' : undefined} aria-label={active.label}>
        {view === 'trabajo' ? <WorkTab refreshKey={refreshKey} userId={user?.id ?? null} /> : <ComplianceTab refreshKey={refreshKey} />}
      </div>
    </PageContainer>
  );
}
